import React from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';
import { NavLink } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { selectors } from '@clearscore/redux.routes';
import SvgReport from '@clearscore/icons.report';
import SvgOffers from '@clearscore/icons.offers';
import SvgCoaching from '@clearscore/icons.coaching';
import SvgTimeline from '@clearscore/icons.timeline';
import SvgProtect from '@clearscore/rainbow.icons.protect';
import Text from '@clearscore/rainbow.text';

import styles from './icon-navigation.css';
import SvgDashboard from './components/dashboard-icon';
import RouteIcon, { navIdentifier } from './components/route-icon.component';
import parsePath from './lib/parse-path';

/**
 * Icon lookup map
 * @type {object}
 */
const iconConfig = {
    report: {
        svg: SvgReport,
    },
    offers: {
        svg: SvgOffers,
        loadAnimation: styles.pulsateMenu,
    },
    dashboard: {
        svg: SvgDashboard,
    },
    coaching: {
        svg: SvgCoaching,
    },
    timeline: {
        svg: SvgTimeline,
    },
    protect: {
        svg: SvgProtect,
    },
};

/**
 * Component to render an icon based navigation
 */
const IconNavigation = ({ className, linkClassName, direction, routerTrackingSource, params, menu }) => {
    const isHorizontal = direction === 'horizontal';
    const navDirectionClass = isHorizontal ? styles.navHorizontal : styles.navVertical;
    const linkDirectionClass = isHorizontal ? styles.linkHorizontal : styles.linkVertical;
    const navigationClass = cx(className, styles.navigation, navDirectionClass);
    const linkClass = cx(linkClassName, styles.navLink, linkDirectionClass);

    const routes = useSelector((state) => selectors.routeSelector(state, { menu }));

    const links = routes.map((route) => {
        const icon = iconConfig[route.icon];
        const dataQa = route.icon || route.name;

        const pathname = parsePath(route.path, params);

        // External links
        if (route.path.startsWith('http')) {
            return (
                <a
                    key={`${navIdentifier}${route.id}`}
                    data-qa={`${navIdentifier}${dataQa}`}
                    href={pathname}
                    className={linkClass}
                    rel="noopener noreferrer"
                    target="_blank"
                >
                    <RouteIcon dataQa={dataQa} direction={direction} icon={icon} />

                    {route.displayName}
                </a>
            );
        }

        return (
            <NavLink
                key={`nav-${route.id}`}
                data-qa={`nav-${dataQa}`}
                activeClassName="is-selected"
                className={linkClass}
                exact={route.path === '/'}
                to={{
                    pathname,
                    state: {
                        tracking: {
                            source: routerTrackingSource,
                        },
                    },
                }}
            >
                <RouteIcon dataQa={dataQa} direction={direction} icon={icon} />

                {isHorizontal ? (
                    <Text {...Text.presets.H4} tag={Text.tags.span}>
                        {route.displayName}
                    </Text>
                ) : (
                    <Text {...Text.presets.H2} tag={Text.tags.span}>
                        {route.displayName}
                    </Text>
                )}
            </NavLink>
        );
    });

    return <div className={navigationClass}>{links}</div>;
};

IconNavigation.propTypes = {
    /** @type {string} direction of menu */
    direction: PropTypes.oneOf(['horizontal', 'vertical']),
    /** @type {string} optional classname for navigation menu */
    className: PropTypes.string,
    /** @type {string} optional classname for each link item */
    linkClassName: PropTypes.string,
    routerTrackingSource: PropTypes.string,
    menu: PropTypes.string,
    params: PropTypes.object, // can be any shape e.g. { market: 'gb' }
};

IconNavigation.defaultProps = {
    direction: 'horizontal',
    routerTrackingSource: '',
    className: null,
    linkClassName: null,
    params: null,
    menu: null,
};

export default IconNavigation;
